import {
  metricTileCardBase,
  metricTileDescription,
  metricTileDisruptionActiveCard,
  metricTileInner,
  metricTileStandardCard,
  metricTileTitle,
} from "@/components/metrics/metricTileStyles";
import { MetricTileValue } from "@/components/metrics/MetricTileValue";
import React, { useEffect, useState } from "react";

interface DisruptionMetricTileProps {
  name: string;
  description: string;
  value: number;
  metricKey: string;
  reportId: number;
  variant?: "disruption" | "standard";
  enablePulse?: boolean;
  onClick?: () => void;
}

export const DisruptionMetricTile: React.FC<DisruptionMetricTileProps> = ({
  name,
  description,
  value,
  metricKey,
  reportId,
  variant = "disruption",
  enablePulse = true,
  onClick,
}) => {
  const [isPulsing, setIsPulsing] = useState(false);
  const isActive = value > 0;
  const isDisruptionVariant = variant === "disruption";
  const storageKey = `disruption_seen_${reportId}_${metricKey}`;

  useEffect(() => {
    if (!enablePulse) {
      setIsPulsing(false);
      return;
    }

    const lastSeen = parseInt(sessionStorage.getItem(storageKey) || "0", 10);
    if (value > lastSeen) {
      setIsPulsing(true);
      const timer = window.setTimeout(() => {
        setIsPulsing(false);
        sessionStorage.setItem(storageKey, String(value));
      }, 10000);
      return () => window.clearTimeout(timer);
    }
    if (lastSeen !== value) {
      sessionStorage.setItem(storageKey, String(value));
    }
    setIsPulsing(false);
  }, [value, storageKey, enablePulse]);

  const cardClasses =
    isActive && isDisruptionVariant
      ? metricTileDisruptionActiveCard
      : metricTileStandardCard;

  const valueColorClass =
    isActive && isDisruptionVariant ? "text-amber-300" : "text-white";

  return (
    <div
      className={`${metricTileCardBase} ${cardClasses}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick?.();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`${name}: ${value.toLocaleString()}`}
    >
      <div className={metricTileInner}>
        <div className={metricTileTitle}>{name}</div>
        <MetricTileValue value={value} className={valueColorClass} />
        <div className={metricTileDescription}>{description}</div>
        {isPulsing && enablePulse && (
          <div className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset bg-amber-500/35 text-amber-50 ring-amber-400/45 animate-pulse mt-3">
            <span className="text-xs font-semibold">New cancellation</span>
          </div>
        )}
      </div>
    </div>
  );
};
